/**
 * Drive Stripe `handleNextAction` for a `requires_action` `clientAction` (TMMRSL-0015): with the
 * open tenant's platform config (`completeClientAction`), or with the reseller-wide config for
 * the reseller's own card (`completeResellerClientAction`, TMMRSL-0046).
 */
import type { ClientAction } from '@/api/generated';
import { billingClientConfigApi, type BillingClientConfigResult } from '@/api/billingClientConfig';
import { getStripe } from './stripeSdk';

export type CompleteActionResult =
  | { kind: 'completed' }
  | { kind: 'failed'; message: string | null }
  | { kind: 'unsupported' };

async function runNextAction(
  config: BillingClientConfigResult,
  clientSecret: string,
  clientAction: ClientAction,
): Promise<CompleteActionResult> {
  if (config.kind !== 'stripe') return { kind: 'unsupported' };
  const account = clientAction.connectedAccountId ?? config.config.stripeAccount;
  const stripe = await getStripe(config.config.publishableKey, account);
  if (!stripe) return { kind: 'failed', message: null };
  const result = await stripe.handleNextAction({ clientSecret });
  if (result.error) {
    return { kind: 'failed', message: result.error.message ?? null };
  }
  return { kind: 'completed' };
}

export async function completeClientAction(
  slug: string,
  clientAction: ClientAction | null | undefined,
): Promise<CompleteActionResult> {
  const secret = clientAction?.clientSecret;
  if (!secret) return { kind: 'unsupported' };
  const config = await billingClientConfigApi.get(slug, 'platform');
  return runNextAction(config, secret, clientAction);
}

/** The reseller's own card (3-D Secure on `PUT /billing/card`): the reseller-wide config. */
export async function completeResellerClientAction(
  clientAction: ClientAction | null | undefined,
): Promise<CompleteActionResult> {
  const secret = clientAction?.clientSecret;
  if (!secret) return { kind: 'unsupported' };
  const config = await billingClientConfigApi.getReseller();
  return runNextAction(config, secret, clientAction);
}
